import styled from "@emotion/styled";
import { useState, useEffect } from "react";
import { useAppStore } from "@/store";
import { RecordingStatus } from "@/store/audio-capture/AppStoreAudioCaptureStore";
import { observer } from "mobx-react-lite";

interface AudioCaptureWaveFormProps {
  className?: string;
  barsColor?: string;
}

export const AudioCaptureWaveForm = observer(
  ({ className, barsColor = "white" }: AudioCaptureWaveFormProps) => {
    const { store } = useAppStore();
    const [waveFormBarHeights, setWaveFormBarHeights] = useState<number[]>();

    useEffect(() => {
      const analyser = store.audioCapture.analyser;
      if (!analyser) return;

      const timeDomainData = new Uint8Array(analyser.fftSize);
      let frameCounter = 0;
      let animationFrameId: number;

      const animate = () => {
        animationFrameId = requestAnimationFrame(animate);

        // Only update every 12th frame
        frameCounter++;
        if (frameCounter % 12 !== 0) return;

        const isRecording = store.audioCapture.status === RecordingStatus.RECORDING;
        analyser.getByteTimeDomainData(timeDomainData);

        if (isRecording) {
          // Divide the waveform data into 3 regions
          const regionSize = Math.floor(timeDomainData.length / 3);
          const minBarHeight = 0.07; // 7% threshold

          // Calculate all bar heights first
          const barHeights = [];
          for (let region = 0; region < 3; region++) {
            const startIdx = region * regionSize;
            const endIdx = startIdx + regionSize;

            // Calculate RMS for this region
            let rms = 0;
            for (let i = startIdx; i < endIdx && i < timeDomainData.length; i++) {
              const sample = (timeDomainData[i] - 128) / 128.0;
              rms += sample * sample;
            }
            rms = Math.sqrt(rms / regionSize);

            // Apply Gaussian curve emphasis based on region position
            const centerDistance = Math.abs(region - 1) / 1; // 0 for center, 1 for edges
            const gaussianWeight = Math.exp(-2 * centerDistance * centerDistance);

            // Boost the amplitude, especially for center region
            const boostedAmplitude = rms * (4 + gaussianWeight * 3);

            // Calculate bar height
            const barHeight = Math.min(boostedAmplitude, 1);
            barHeights.push(barHeight);
          }

          // Check if all bars are too small (less than 7% each)
          const allBarsSmall = barHeights.every(height => height < minBarHeight);

          // Only render a minimal waveform unless at least one bar is big enough
          setWaveFormBarHeights(allBarsSmall ? BarsZeroState : barHeights);
        } else {
          setWaveFormBarHeights(undefined);
        }
      };

      animate();
      return () => {
        cancelAnimationFrame(animationFrameId);
        setWaveFormBarHeights(undefined);
      };
    }, [store.audioCapture.analyser, store.audioCapture.status]);

    return (
      <WaveFormContainer className={className}>
        {(waveFormBarHeights ?? BarsZeroState).map((height, index) => (
          <WaveFormBar
            key={index}
            style={{ height: `${height * 100}%`, backgroundColor: barsColor }}
          />
        ))}
      </WaveFormContainer>
    );
  }
);

const BarsZeroState = [0.1, 0.1, 0.1];

const WaveFormContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 0 1px;
  width: 16px;
  height: 16px;
  gap: 1px;
`;

const WaveFormBar = styled.div`
  border-radius: 4px;
  width: 4px;
  transition: height 0.1s ease-in-out;
`;
